import type { CSSProperties } from 'react';
import { GAME_SPLASH_MS, type GameSplashVM } from '@tv-casino/viewmodels/components/gameSplashModel.js';
import styles from './GameSplash.module.css';

/**
 * The card the screen holds up while the table changes game: what is being
 * dealt now, what it is, and what the player does next. The TV runs the big
 * one over the whole felt; a phone runs the same words as a strip over its
 * controls, so nothing is tapped blind while the buttons underneath are being
 * replaced.
 *
 * The whole card lives on one animation timed to `holdMs`, so it fades itself
 * out on the same beat the owning hook takes it down — the screen never
 * snaps back to the felt.
 */
export function GameSplash({
  vm,
  size = 'tv',
  holdMs = GAME_SPLASH_MS,
  onDismiss,
}: {
  vm: GameSplashVM;
  size?: 'tv' | 'phone';
  holdMs?: number;
  /** Given, the card can be tapped away — the phone's exit from a swap. */
  onDismiss?: () => void;
}) {
  return (
    <div
      className={`${styles.wrap} ${size === 'phone' ? styles.phone : styles.tv}`}
      style={{ '--splash-ms': `${holdMs}ms` } as CSSProperties}
      role="status"
      aria-live="polite"
    >
      {onDismiss && (
        <button
          type="button"
          className={styles.skip}
          onClick={onDismiss}
          aria-label="Skip the game switch card"
        />
      )}
      <div className={styles.card}>
        <p className={styles.kicker}>{vm.kicker}</p>
        <p className={styles.title}>{vm.title}</p>
        <p className={styles.tagline}>{vm.tagline}</p>
        <p className={styles.hint}>{vm.hint}</p>
        {vm.rule && <p className={styles.rule}>{vm.rule}</p>}
        {onDismiss && <p className={styles.skipHint}>Tap to skip</p>}
      </div>
    </div>
  );
}
